import React, { useEffect, useRef, useState } from "react";
import { Link as RouterLink } from "react-router-dom";

import {
  Box,
  useTheme,
  CircularProgress,
  Typography,
  Link,
  TextField,
  Button,
  InputAdornment,
  IconButton,
  LinearProgress,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import {
  doSignInWithEmailLink,
  doConfirmPasswordReset,
} from "../../firebase/auth";
import { useAuth } from "../../contexts/AuthContext";
import { useStateContext } from "contexts/ContextProvider";
import api from "services/api";
import { usePageTitle } from "hooks/usePageTitle";
import Visibility from "@mui/icons-material/VisibilityRounded";
import VisibilityOff from "@mui/icons-material/VisibilityOffRounded";
import { applyActionCode, checkActionCode, getAuth, signOut } from "firebase/auth";

// Password strength evaluation
const evaluatePasswordStrength = (password) => {
  if (!password) return { strength: 0, label: "", color: "grey" };

  let strength = 0;
  const checks = {
    length: password.length >= 8,
    uppercase: /[A-Z]/.test(password),
    lowercase: /[a-z]/.test(password),
    number: /[0-9]/.test(password),
    special: /[^A-Za-z0-9]/.test(password),
  };

  // Length is worth 2 points, others 1 each
  if (checks.length) strength += 2;
  if (checks.uppercase) strength += 1;
  if (checks.lowercase) strength += 1;
  if (checks.number) strength += 1;
  if (checks.special) strength += 1;

  // Additional length bonuses
  if (password.length >= 12) strength += 1;
  if (password.length >= 16) strength += 1;

  // Map strength to label and color
  if (strength <= 2) {
    return { strength: 25, label: "Weak", color: "error", checks };
  } else if (strength <= 4) {
    return { strength: 50, label: "Fair", color: "warning", checks };
  } else if (strength <= 6) {
    return { strength: 75, label: "Good", color: "info", checks };
  } else {
    return { strength: 100, label: "Strong", color: "success", checks };
  }
};

const LoginConfirmation = () => {
  usePageTitle("Login Confirmation");
  const { userLoggedIn } = useAuth();
  const { showSnackbar } = useStateContext();

  const theme = useTheme();
  const email = window.localStorage.getItem("Email") || "";
  const [message, setMessage] = useState("");
  const [title, setTitle] = useState("Please Wait");
  const [isLoading, setIsLoading] = useState(true);
  const [member, setMember] = useState();

  const [isPasswordReset, setIsPasswordReset] = useState(false);
  const [resetCode, setResetCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const hasProcessedVerifyActionRef = useRef(false);
  const hasProcessedSignInLinkRef = useRef(false);

  const params = new URLSearchParams(window.location.search);
  const mode = params.get("mode");
  const oobCode = params.get("oobCode");

  const passwordStrength = evaluatePasswordStrength(newPassword);
  const passwordsDoNotMatch =
    newPassword && confirmPassword && newPassword !== confirmPassword;

  useEffect(() => {
    if (!userLoggedIn) return;
    if (mode === "verifyAndChangeEmail") return;

    const forceLogout = async () => {
      try {
        await signOut(getAuth());
        setTitle("Signed Out");
        setMessage("Session ended. Please continue from this page.");
      } catch (error) {
        console.error("Error signing out from Login Confirmation:", error);
      }
    };

    forceLogout();
  }, [mode, userLoggedIn]);

  useEffect(() => {
    // Check if this is a password reset flow - skip member data fetch
    if (mode === "resetPassword" && oobCode) {
      return; // Don't fetch member data for password reset
    }

    if (mode === "verifyAndChangeEmail" && oobCode) {
      setTitle("Confirming Email Change");
      setMessage("Please wait while we verify your new email address...");
      setIsLoading(true);
      return;
    }

    // For email link sign-in, fetch member data only if email exists
    if (email) {
      const fetchData = () => {
        api
          .get(`/members/email/${email}`)
          .then((response) => {
            setMember(response.data);
          })
          .catch((error) => {
            console.error("Error fetching member data:", error);
          });
      };

      fetchData();
    }
  }, [email, mode, oobCode]);

  //------------------------- FIREBASE STUFF ---------------------------------

  const handleResetPasswordSubmit = async () => {
    if (!newPassword || newPassword.length < 6) {
      return;
    }

    if (newPassword !== confirmPassword) {
      return;
    }

    try {
      setIsLoading(true);
      await doConfirmPasswordReset(resetCode, newPassword);
      setTitle("Password Reset Successfully");
      setMessage("Redirecting to login...");
      setTimeout(() => {
        window.location.href = "/login";
      }, 2000);
    } catch (error) {
      setTitle("Error Resetting Password");
      setMessage(error.message || "Failed to reset password");
      setIsLoading(false);
    }
  };

  useEffect(() => {
    // Check if this is a password reset flow
    if (mode === "resetPassword" && oobCode) {
      setIsPasswordReset(true);
      setResetCode(oobCode);
      setTitle("Reset Your Password");
      setIsLoading(false);
      return;
    }

    if (mode === "verifyAndChangeEmail" && oobCode) {
      if (hasProcessedVerifyActionRef.current) return;
      hasProcessedVerifyActionRef.current = true;

      const verifyEmailChange = async () => {
        try {
          const auth = getAuth();
          const actionCodeInfo = await checkActionCode(auth, oobCode);
          const verifiedEmail = String(actionCodeInfo?.data?.email || "").trim();
          const previousEmail = String(
            actionCodeInfo?.data?.previousEmail || "",
          ).trim();

          await applyActionCode(auth, oobCode);

          // Keep Portal DB email aligned with Firebase email verification result.
          if (
            verifiedEmail &&
            previousEmail &&
            verifiedEmail.toLowerCase() !== previousEmail.toLowerCase()
          ) {
            let isAlreadySynced = false;

            try {
              await api.get(`/members/email/${encodeURIComponent(verifiedEmail)}`);
              isAlreadySynced = true;
            } catch (lookupErr) {
              if (lookupErr?.response?.status !== 404) {
                throw lookupErr;
              }
            }

            if (!isAlreadySynced) {
              const previousMemberRes = await api.get(
                `/members/email/${encodeURIComponent(previousEmail)}`,
              );
              const previousMember = previousMemberRes.data;

              await api.put(`/members/${previousMember.id}`, {
                name: previousMember.Name,
                role: previousMember.Role,
                email: verifiedEmail,
                skipFirebaseEmailSync: true,
              });
            }
          }

          if (verifiedEmail) {
            window.localStorage.setItem("Email", verifiedEmail);
          }

          setTitle("Email Verified");
          setMessage(
            "Your email address has been verified. Please sign in using the new email.",
          );
          showSnackbar(
            "Email changed successfully. Please sign in with your new email.",
            theme,
            "success",
          );

          try {
            await signOut(auth);
          } catch (_) {
            // best-effort sign out only
          }

          setTimeout(() => {
            window.location.href = "/login";
          }, 2000);
        } catch (error) {
          console.error("Email verification error:", error);
          setTitle("Verification Error");
          setMessage(
            error?.response?.data?.error ||
              error?.message ||
              "Failed to verify email change.",
          );
          setIsLoading(false);
        }
      };

      verifyEmailChange();
      return;
    }

    // Otherwise, handle email link sign-in
    if (!member) return;
    if (hasProcessedSignInLinkRef.current) return;
    hasProcessedSignInLinkRef.current = true;

    setTimeout(async () => {
      await doSignInWithEmailLink(
        window.localStorage.getItem("Email"),
        window.location.href,
      )
        .then(() => {
          window.localStorage.setItem("Email", member.Email);
          window.localStorage.setItem("Name", member.Name);
          window.localStorage.setItem("Role", member.Role);
          window.localStorage.setItem("id", member.id);
        })
        .catch((error) => {
          console.error("Email link sign-in error:", error);
          setTitle("Error");
          setMessage(error?.message || "Could not complete email link sign-in.");
          setIsLoading(false);
        });
    }, 1000);
  }, [
    member?.Email,
    member?.Name,
    member?.Role,
    member?.id,
    member,
    mode,
    oobCode,
    showSnackbar,
    theme,
  ]);

  return (
    <Box
      display="flex"
      flexDirection="column"
      minHeight="100vh"
      sx={{
        //backgroundColor: theme.palette.background.default,
        position: "relative",
      }}
    >
      {/* Logo - centered on mobile, left on larger screens */}
      <Box
        sx={{
          position: "absolute",
          top: { xs: 16, sm: 24, md: 32 },
          left: { xs: "50%", sm: 24, md: 32 },
          transform: { xs: "translateX(-50%)", sm: "none" },
          width: { xs: "180px", sm: "240px", md: "360px" },
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          zIndex: 10,
        }}
      >
        <img
          src="/assets/ce-logo-full-h.svg"
          alt="CE Portal Logo"
          style={{ maxWidth: "100%", maxHeight: "100%" }}
        />
      </Box>

      {/* Main content using Grid */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          px: { xs: 2, sm: 3 },
        }}
      >
        <Grid
          container
          sx={{
            width: "100%",
            maxWidth: "1200px",
            minHeight: { xs: "auto", sm: "50vh" },
            backgroundColor: theme.palette.background.default,
            borderRadius: { xs: "1rem", sm: "1.25rem", md: "1.5rem" },
            boxShadow: 3,
            overflow: "hidden",
          }}
        >
          {/* Left decorative section - Only visible on xl */}
          <Grid
            size={{ xs: 0, xl: 6 }}
            sx={{
              display: { xs: "none", xl: "flex" },
              pt: { xl: 4 },
              pb: { xl: 8 },
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              textAlign: "center",
              position: "relative",
              overflow: "hidden",
              minHeight: { xl: "300px" },
              borderRight: { xl: `1px solid ${theme.palette.divider}` },
            }}
          >
            {/* Background image container */}
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                opacity: 0.1,
                scale: 0.75,
                backgroundImage: 'url("/assets/ce-logo.svg")',
                backgroundSize: "contain",
                backgroundPosition: "center",
                backgroundRepeat: "no-repeat",
                zIndex: 1,
                animation: `zoomInOut 12s ease-in-out infinite`,
                "@keyframes zoomInOut": {
                  "0%": {
                    transform: "scale(1)",
                  },
                  "50%": {
                    transform: "scale(0.85)",
                  },
                  "100%": {
                    transform: "scale(1)",
                  },
                },
              }}
            />
          </Grid>

          {/* Right message content section */}
          <Grid
            size={{ xs: 12, xl: 6 }}
            sx={{
              p: { xs: 2, sm: 3, md: 4 },
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                width: "100%",
                gap: { xs: 1.5, sm: 2 },
              }}
            >
              {/* Header */}
              <Typography
                variant="h2"
                sx={{
                  fontWeight: "bold",
                  fontSize: { xs: "1.75rem", sm: "2rem", md: "2.5rem" },
                  color: theme.palette.primary.main,
                  textAlign: "center",
                }}
              >
                {title}
              </Typography>

              {!!message && (
                <Typography
                  variant="body1"
                  sx={{
                    color: theme.palette.text.secondary,
                    textAlign: "center",
                    whiteSpace: "pre-line",
                  }}
                >
                  {message}
                </Typography>
              )}

              {/* Content */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  width: "100%",
                }}
              >
                {isLoading ? (
                  <CircularProgress
                    size="5rem"
                    sx={{
                      color: theme.palette.primary.main,
                    }}
                  />
                ) : isPasswordReset ? (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 2,
                      width: "100%",
                      maxWidth: "500px",
                    }}
                  >
                    <TextField
                      type={showPassword ? "text" : "password"}
                      label="New Password"
                      placeholder="Enter new password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      variant="outlined"
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          border: "1px solid",
                          borderColor: theme.palette.divider,
                        },
                      }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => setShowPassword(!showPassword)}
                              edge="end"
                            >
                              {showPassword ? (
                                <VisibilityOff />
                              ) : (
                                <Visibility />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                    <TextField
                      type={showConfirmPassword ? "text" : "password"}
                      label="Confirm New Password"
                      placeholder="Confirm new password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      error={passwordsDoNotMatch}
                      helperText={
                        passwordsDoNotMatch ? "Passwords do not match" : ""
                      }
                      variant="outlined"
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          border: "1px solid",
                          borderColor: theme.palette.divider,
                        },
                      }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() =>
                                setShowConfirmPassword(!showConfirmPassword)
                              }
                              edge="end"
                            >
                              {showConfirmPassword ? (
                                <VisibilityOff />
                              ) : (
                                <Visibility />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                    {newPassword && (
                      <Box sx={{ mt: 0.5 }}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            mb: 0.5,
                          }}
                        >
                          <Typography variant="body2" color="text.secondary">
                            Password Strength:
                          </Typography>
                          <Typography
                            variant="body2"
                            fontWeight="bold"
                            color={`${passwordStrength.color}.main`}
                          >
                            {passwordStrength.label}
                          </Typography>
                        </Box>
                        <LinearProgress
                          variant="determinate"
                          value={passwordStrength.strength}
                          sx={{
                            height: 6,
                            borderRadius: 1,
                            backgroundColor: theme.palette.action.hover,
                            "& .MuiLinearProgress-bar": {
                              backgroundColor:
                                theme.palette[passwordStrength.color].main,
                            },
                          }}
                        />
                        <Box sx={{ mt: 1 }}>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                          >
                            Password should contain:
                          </Typography>
                          <Typography
                            variant="caption"
                            color={
                              passwordStrength.checks?.length
                                ? "success.main"
                                : "text.secondary"
                            }
                          >
                            ✓ At least 8 characters
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {" "}
                            |{" "}
                          </Typography>
                          <Typography
                            variant="caption"
                            color={
                              passwordStrength.checks?.uppercase
                                ? "success.main"
                                : "text.secondary"
                            }
                          >
                            ✓ Uppercase
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {" "}
                            |{" "}
                          </Typography>
                          <Typography
                            variant="caption"
                            color={
                              passwordStrength.checks?.lowercase
                                ? "success.main"
                                : "text.secondary"
                            }
                          >
                            ✓ Lowercase
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {" "}
                            |{" "}
                          </Typography>
                          <Typography
                            variant="caption"
                            color={
                              passwordStrength.checks?.number
                                ? "success.main"
                                : "text.secondary"
                            }
                          >
                            ✓ Number
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {" "}
                            |{" "}
                          </Typography>
                          <Typography
                            variant="caption"
                            color={
                              passwordStrength.checks?.special
                                ? "success.main"
                                : "text.secondary"
                            }
                          >
                            ✓ Special character
                          </Typography>
                        </Box>
                      </Box>
                    )}
                    <Button
                      variant="contained"
                      onClick={handleResetPasswordSubmit}
                      disabled={
                        !newPassword ||
                        newPassword.length < 6 ||
                        passwordsDoNotMatch
                      }
                      sx={{
                        borderRadius: "12px",
                        py: 1.5,
                        fontSize: "1.1rem",
                      }}
                    >
                      Reset Password
                    </Button>
                  </Box>
                ) : (
                  <Typography
                    variant="h6"
                    align="center"
                    sx={{
                      fontSize: { xs: "1rem", sm: "1.2rem", md: "1.3rem" },
                      color: theme.palette.warning[400],
                      whiteSpace: "pre-line",
                    }}
                  >
                    {"Your confirmation link timed out.\n"}
                    <Link component={RouterLink} to="/login">
                      {"Click here to log in again."}
                    </Link>
                  </Typography>
                )}
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default LoginConfirmation;
